import { useState, useRef, useEffect } from 'react'
import { Draggable } from '@hello-pangea/dnd'
import { format } from 'date-fns'

const KanbanCard = ({ gym, index, canDrag = true, onViewDetails, onLogVisit, onUploadDocuments, onRemoveGym, onInternalApprove, onInternalReject, onPreRecce, userRole }) => {
  const isCEO = userRole?.level === 1;
  const isCOO = userRole?.level === 2;
  const isHoA = userRole?.level === 3;
  const canApproveInternal = isCEO || isCOO || isHoA;
  const [showMenu, setShowMenu] = useState(false)
  const menuRef = useRef(null)

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false)
      }
    }

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showMenu])

  const getStageColor = (stage) => {
    const colors = {
      'New Lead': 'bg-gray-100 text-gray-800',
      'Site Visit': 'bg-blue-100 text-blue-800',
      'Internal Approval': 'bg-amber-100 text-amber-800',
      'CEO Approval': 'bg-pink-100 text-pink-800',
      'Proposal Sent': 'bg-yellow-100 text-yellow-800',
      'Agreement Signed': 'bg-green-100 text-green-800',
      'Facility Work': 'bg-purple-100 text-purple-800',
      'Post Reki Approved': 'bg-indigo-100 text-indigo-800',
      'Gym Onboard': 'bg-red-100 text-red-800'
    }
    return colors[stage] || 'bg-gray-100 text-gray-800'
  }

  const CardContent = ({ isDragging = false }) => (
    <div className={`kanban-card max-w-full ${
      !canDrag
        ? 'cursor-not-allowed opacity-75'
        : isDragging
          ? 'cursor-grabbing dragging'
          : 'cursor-grab active:cursor-grabbing hover:border-blue-300'
    }`}>
      <div className="mb-2">
        <div className="flex items-start gap-2">
          <h3 className="text-sm font-medium text-gray-900 flex-1 min-w-0 break-words leading-tight">
            {gym.name}
          </h3>
          {/* Three-dot menu */}
          <div className="relative" ref={menuRef}>
            <button
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
                setShowMenu(!showMenu)
              }}
              className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path d="M6 10a2 2 0 11-4 0 2 2 0 014 0zM12 10a2 2 0 11-4 0 2 2 0 014 0zM16 12a2 2 0 100-4 2 2 0 000 4z" />
              </svg>
            </button>
            {showMenu && (
              <div className="absolute right-0 top-6 w-40 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    e.preventDefault()
                    setShowMenu(false)
                    onRemoveGym && onRemoveGym(gym)
                  }}
                  className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 rounded-lg flex items-center space-x-2 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  <span>Remove Gym</span>
                </button>
              </div>
            )}
          </div>
        </div>
        <div className="mt-1.5">
          <span className={`badge ${getStageColor(gym.stage)} text-xs inline-block`}>
            {gym.stage}
          </span>
        </div>
      </div>
      
      <div className="space-y-1 text-xs text-gray-600">
        <div className="flex items-center">
          <span className="w-12 text-gray-500">City:</span>
          <span className="truncate">{gym.city}</span>
        </div>
        
        {gym.assignedManager && (
          <div className="flex items-center">
            <span className="w-12 text-gray-500">Manager:</span>
            <span className="truncate">
              {gym.assignedManager.name}
            </span>
          </div>
        )}
        
        {gym.targetLaunchDate && (
          <div className="flex items-center">
            <span className="w-12 text-gray-500">Target:</span>
            <span className="text-blue-600">
              {format(new Date(gym.targetLaunchDate), 'MMM dd, yyyy')}
            </span>
          </div>
        )}
        
        <div className="flex items-center">
          <span className="w-12 text-gray-500">Created:</span>
          <span>{format(new Date(gym.createdAt), 'MMM dd')}</span>
        </div>

        {/* Enhanced Visit Information for Site Visit stage */}
        {gym.stage === 'Site Visit' && (
          <div className="space-y-2">
            {/* Visit Progress */}
            <div className="flex items-center justify-between">
              <span className="text-gray-500 text-xs">Visits Done:</span>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                (gym.visitLogs?.length || 0) >= 2 
                  ? 'bg-red-100 text-red-800' 
                  : (gym.visitLogs?.length || 0) >= 1
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-green-100 text-green-800'
              }`}>
                {gym.visitLogs?.length || 0} / 3
              </span>
            </div>

            {/* Visit Progress Visual */}
            <div className="flex items-center space-x-1">
              {[1, 2, 3].map((visitNum) => {
                const visitData = gym.visitLogs?.find(v => v.attemptNumber === visitNum);
                const isCompleted = !!visitData;
                
                let dotColor = 'bg-gray-300';
                if (isCompleted) {
                  switch (visitData.visitType) {
                    case 'owner_met':
                      dotColor = 'bg-green-500';
                      break;
                    case 'owner_not_met':
                      dotColor = 'bg-red-500';
                      break;
                    case 'photos_only':
                      dotColor = 'bg-blue-500';
                      break;
                  }
                }
                
                return (
                  <div key={visitNum} className="flex items-center">
                    <div className={`w-3 h-3 rounded-full ${dotColor}`}></div>
                    {visitNum < 3 && <div className="w-2 h-0.5 bg-gray-300 mx-1"></div>}
                  </div>
                );
              })}
            </div>

            {/* Visit History */}
            <div className="space-y-1">
              <div className="text-xs text-gray-500 font-medium">Visit History:</div>
              {[1, 2, 3].map((visitNum) => {
                const visitData = gym.visitLogs?.find(v => v.attemptNumber === visitNum);
                const isCompleted = !!visitData;
                
                let dotColor = 'bg-gray-300';
                let statusText = 'Pending';
                
                if (isCompleted) {
                  const visitDate = new Date(visitData.visitDate);
                  const formattedDate = visitDate.toLocaleDateString('en-US', { 
                    day: '2-digit', 
                    month: 'short' 
                  });
                  const formattedTime = visitDate.toLocaleTimeString('en-US', { 
                    hour: '2-digit', 
                    minute: '2-digit',
                    hour12: true 
                  });
                  
                  switch (visitData.visitType) {
                    case 'owner_met':
                      dotColor = 'bg-green-500';
                      statusText = `Owner Met (${formattedDate}, ${formattedTime})`;
                      break;
                    case 'owner_not_met':
                      dotColor = 'bg-red-500';
                      statusText = `Owner Not Available (${formattedDate}, ${formattedTime})`;
                      break;
                    case 'photos_only':
                      dotColor = 'bg-blue-500';
                      statusText = `Photos Only (${formattedDate}, ${formattedTime})`;
                      break;
                  }
                }
                
                return (
                  <div key={visitNum} className="flex items-center text-xs">
                    <span className={`w-2 h-2 rounded-full mr-2 ${dotColor}`}></span>
                    <span className="text-gray-600">
                      {visitNum === 1 ? '1st' : visitNum === 2 ? '2nd' : '3rd'} Visit – {statusText}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
      
      {/* Pre-Recce Progress for Facility Work stage */}
      {gym.stage === 'Facility Work' && (() => {
        const forms = gym.preRecceForms || [];
        const formTypes = [
          { key: 'electrical', label: 'Electrical', icon: '⚡' },
          { key: 'plumbing', label: 'Plumbing', icon: '🔧' },
          { key: 'equipment_inventory', label: 'Equipment', icon: '🏋️' }
        ];
        const approvedCount = forms.filter(f => f.status === 'approved').length;
        return (
          <div className="mt-2 pt-2 border-t border-gray-100">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs font-medium text-gray-600">Pre-Recce Forms</span>
              <span className={`text-xs font-semibold ${
                approvedCount === 3 ? 'text-green-600' : approvedCount > 0 ? 'text-amber-600' : 'text-gray-400'
              }`}>{approvedCount}/3</span>
            </div>
            <div className="flex gap-1.5">
              {formTypes.map(ft => {
                const form = forms.find(f => f.formType === ft.key);
                const status = form?.status || 'none';
                const statusColors = {
                  none: 'bg-gray-100 text-gray-400 border-gray-200',
                  draft: 'bg-yellow-50 text-yellow-700 border-yellow-200',
                  submitted: 'bg-blue-50 text-blue-700 border-blue-200',
                  approved: 'bg-green-50 text-green-700 border-green-200',
                  rejected: 'bg-red-50 text-red-700 border-red-200'
                };
                return (
                  <div key={ft.key} className={`flex-1 text-center py-1 rounded border text-[10px] font-medium ${statusColors[status]}`}
                    title={`${ft.label}: ${status === 'none' ? 'Not started' : status}`}>
                    <span>{ft.icon}</span>
                    {status === 'approved' && <span className="ml-0.5">✓</span>}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Progress indicator */}
      <div className="mt-2 pt-2 border-t border-gray-100">
        <div className="flex items-center justify-between text-xs">
          <span className="text-gray-500">
            {gym._count?.checklistItems || 0} tasks
          </span>
          {gym.targetLaunchDate && (
            <span className={`${
              new Date(gym.targetLaunchDate) < new Date() 
                ? 'text-red-500' 
                : 'text-gray-500'
            }`}>
              {new Date(gym.targetLaunchDate) < new Date() ? 'Overdue' : 'On track'}
            </span>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="mt-2 pt-2 border-t border-gray-100 space-y-2">
        {/* Log Visit Button only for Site Visit stage and only if owner has NOT been met yet */}
        {gym.stage === 'Site Visit' && !gym.visitLogs?.some(v => v.visitType === 'owner_met') && (
          <button
            type="button"
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation()
              e.preventDefault()
              console.log('Log Visit clicked for gym:', gym.name)
              if (onLogVisit) onLogVisit(gym)
            }}
            className="w-full py-2.5 px-3 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors duration-150 flex items-center justify-center space-x-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>Log Visit</span>
          </button>
        )}

        {/* HoA/COO Approve Button for Internal Approval stage */}
        {gym.stage === 'Internal Approval' && (
          <button
            onClick={(e) => {
              e.stopPropagation()
              e.preventDefault()
              onInternalApprove && onInternalApprove(gym)
            }}
            disabled={!canApproveInternal}
            className={`w-full py-2.5 px-3 text-sm font-medium rounded-lg transition-colors duration-150 flex items-center justify-center space-x-2 ${
              canApproveInternal
                ? 'text-white bg-amber-600 hover:bg-amber-700'
                : 'text-gray-400 bg-gray-100 cursor-not-allowed'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>{canApproveInternal ? 'HoA / COO Approve' : 'HoA / COO Only'}</span>
          </button>
        )}

        {/* Reject Button for Internal Approval / CEO Approval stages */}
        {(gym.stage === 'Internal Approval' || gym.stage === 'CEO Approval') && canApproveInternal && (
          <button
            onClick={(e) => {
              e.stopPropagation()
              e.preventDefault()
              onInternalReject && onInternalReject(gym)
            }}
            className="w-full py-2.5 px-3 text-sm font-medium rounded-lg transition-colors duration-150 flex items-center justify-center space-x-2 text-white bg-red-600 hover:bg-red-700"
            title="Reject this gym and move it to Expired Clients"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            <span>Reject</span>
          </button>
        )}

        {/* CEO Approve Button for CEO Approval stage */}
        {gym.stage === 'CEO Approval' && (
          <button
            onClick={(e) => {
              e.stopPropagation()
              e.preventDefault()
              onInternalApprove && onInternalApprove(gym)
            }}
            disabled={!isCEO}
            className={`w-full py-2.5 px-3 text-sm font-medium rounded-lg transition-colors duration-150 flex items-center justify-center space-x-2 ${
              isCEO
                ? 'text-white bg-pink-600 hover:bg-pink-700'
                : 'text-gray-400 bg-gray-100 cursor-not-allowed'
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>{isCEO ? 'CEO Approve' : 'CEO Only'}</span>
          </button>
        )}

        {/* Upload Documents Button for Agreement Signed stage */}
        {gym.stage === 'Agreement Signed' && (
          <button
            onClick={(e) => {
              e.stopPropagation()
              e.preventDefault()
              onUploadDocuments && onUploadDocuments(gym)
            }}
            className="w-full py-1.5 px-2 text-xs font-medium text-green-600 bg-green-50 hover:bg-green-100 rounded transition-colors duration-150 flex items-center justify-center space-x-1"
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span>Upload Documents</span>
          </button>
        )}

        {/* Pre-Recce Forms Button for Facility Work stage */}
        {gym.stage === 'Facility Work' && (
          <button
            onClick={(e) => {
              e.stopPropagation()
              e.preventDefault()
              onPreRecce && onPreRecce(gym)
            }}
            className="w-full py-2 px-2 text-xs font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors duration-150 flex items-center justify-center space-x-1.5 border border-purple-200"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
            <span>Pre-Recce Forms</span>
          </button>
        )}

        {/* View Details Button */}
        <button
          onClick={(e) => {
            e.stopPropagation()
            e.preventDefault()
            onViewDetails && onViewDetails(gym)
          }}
          className="w-full py-1.5 px-2 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded transition-colors duration-150 flex items-center justify-center space-x-1"
        >
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
          <span>View Details</span>
        </button>
      </div>
      
      {!canDrag && (
        <div className="mt-1 text-xs text-gray-500 italic">
          🔒 Cannot move this gym
        </div>
      )}
      
      {canDrag && (
        <div className="mt-1 text-xs text-blue-600 italic">
          ↕️ Drag to move
        </div>
      )}
    </div>
  )

  if (!canDrag) {
    return <CardContent isDragging={false} />
  }

  return (
    <Draggable 
      draggableId={String(gym.id)} 
      index={index}
      isDragDisabled={!canDrag}
    >
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          className={`${snapshot.isDragging ? 'rotate-2 shadow-lg z-50' : ''} transition-transform duration-200`}
          style={{
            ...provided.draggableProps.style,
            transform: snapshot.isDragging 
              ? `${provided.draggableProps.style?.transform} rotate(2deg)` 
              : provided.draggableProps.style?.transform
          }}
        >
          <CardContent isDragging={snapshot.isDragging} />
        </div>
      )}
    </Draggable>
  )
}

export default KanbanCard 